import { Fragment, ReactNode, useEffect } from 'react'
import { RouteObject, useSearchParams } from 'react-router-dom'

import BasicLayout from '@/presentation/components/ui/layout/basic-layout'
import AuthLayout from '@/presentation/components/ui/layout/auth-layout'
import { RecaptchaProvider } from '@/presentation/contexts/recaptcha/recaptcha-provider'

import * as Pages from './pages'

import { PathConstants } from './paths'
import { getSessionId } from '@/data/services/login/session-id-store'
import { getSafeRedirectUri } from '@/data/services/login/login-service-utils'

const RedirectIfAuthenticated = ({ children }: { children: ReactNode }) => {
  const [searchParams] = useSearchParams()
  const isAuthenticated = !!getSessionId()

  useEffect(() => {
    if (isAuthenticated) {
      const redirectUri = getSafeRedirectUri(searchParams, PathConstants.Routes.HOME.value)
      window.location.replace(redirectUri)
    }
  }, [isAuthenticated, searchParams])

  if (isAuthenticated) return null

  return <Fragment>{children}</Fragment>
}

// ============================================
// UNAUTHENTICATED ROUTES BUILDER
// ============================================

/**
 * All unauthenticated (public) routes
 * These routes are accessible without authentication
 *
 * @returns Array of RouteObjects for React Router
 */

export const UnauthenticatedRoutes = (): RouteObject[] => {
  const routes = [
    {
      element: (
        <RedirectIfAuthenticated>
          <RecaptchaProvider>
            <AuthLayout />
          </RecaptchaProvider>
        </RedirectIfAuthenticated>
      ),
      children: [
        {
          path: PathConstants.Routes.LOGIN.value,
          element: <Pages.LoginPage />
        },
        {
          path: PathConstants.Routes.FORGOT_PASSWORD.value,
          element: <Pages.ForgotPasswordPage />
        },
        {
          path: PathConstants.Routes.RESET_PASSWORD.value,
          element: <Pages.ResetPasswordPage />
        },
        {
          path: PathConstants.Routes.RESEND_ACCESS.value,
          element: <Pages.ResendAccessPage />
        },
        {
          path: PathConstants.Routes.LOGIN_FIRST_ACCESS.value,
          element: <Pages.LoginFirstAccessPage />
        }
      ]
    },
    {
      element: (
        <RedirectIfAuthenticated>
          <BasicLayout footerVariant="compact" />
        </RedirectIfAuthenticated>
      ),
      children: [
        {
          path: PathConstants.Routes.FIRST_ACCESS.value,
          element: <Pages.FirstAccessPage />
        },
        {
          path: PathConstants.Routes.FIRST_ACCESS_CPF.value,
          element: <Pages.FirstAccessCpfPage />
        }
      ]
    },
    {
      path: PathConstants.Routes.CB_UNICO_SUCCESS.value,
      element: <Pages.CbUnicoSuccessPage />
    },
    {
      path: PathConstants.Routes.SKILL_EXPLORER_CATALOG.value,
      element: <Pages.SkillExplorerCatalogPage />
    }
  ]

  return [...routes]
}
